import { useCallback, useEffect, useRef, useState } from 'react';

import {
  acquireHotelPage,
  hotelPageCoalesceKey,
  type AcquiredPage,
} from './hotel_page_coordinator.js';
import { useKeyedValue } from '../../shared/use_keyed_value.js';

/** Rejeição de uma requisição abortada pelo coordenador. Ver o guard no catch de
 *  `load` para por que ele fica (INOBSERVÁVEL hoje, não redundante). */
function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError';
}

export type HotelOfferPagesQuery<C> = { cuts?: C; order?: string };

/** O que o hook pede ao host: a página `page.number` da sessão, com o corte e a
 *  ordem da consulta. É o formato da leitura paginada do BFF. */
export type HotelOfferPageRequest<C> = {
  sessionId: string;
  page: { number: number; limit: number };
  cuts?: C;
  order?: string;
};

export type HotelOfferPageResult<T> = {
  offers: readonly T[];
  page: { hasNext: boolean; total?: number };
};

export type HotelOfferPagePhase = 'idle' | 'loading' | 'loadingMore' | 'ready' | 'error' | 'stale';

export type HotelOfferPagesState<T> = {
  /** Páginas acumuladas na ordem em que chegaram. Vazio enquanto a primeira não
   *  volta — quem decide o que mostrar nesse meio-tempo é o chamador (o painel
   *  segue com as ofertas do bloco). */
  offers: readonly T[];
  /** `stale` = a busca desta sessão venceu no fornecedor. Estado próprio, e não
   *  `error`, porque as reações são opostas: falha se repete, vencida se refaz. */
  phase: HotelOfferPagePhase;
  hasMore: boolean;
  total?: number;
  /** A paginação já RESPONDEU alguma vez nesta sessão — com página ou com
   *  falha. É o que separa "ainda não perguntei" (o painel mostra o bloco, que
   *  é o paint rápido) de "esta é a resposta", inclusive vazia. Uma vez ligado
   *  não desliga: trocar de corte não devolve o painel para a lista resumida. */
  committed: boolean;
};

export type HotelOfferPages<T> = HotelOfferPagesState<T> & {
  requestKey: string;
  loadMore: () => void;
  retry: () => void;
};

const IDLE: HotelOfferPagesState<never> = {
  offers: [],
  phase: 'idle',
  hasMore: false,
  committed: false,
};

/** `current` seguido do que `incoming` traz de inédito, por `offerId`. O
 *  servidor reusa o handle por hotel já visto, então o mesmo hotel relido numa
 *  página seguinte colapsa na entrada que já estava em cena. */
export function mergeHotelOffers<T extends { offerId: string }>(
  current: readonly T[],
  incoming: readonly T[],
): T[] {
  const seen = new Set(current.map((offer) => offer.offerId));
  return [...current, ...incoming.filter((offer) => !seen.has(offer.offerId))];
}

/**
 * Esta requisição deve ser abandonada por já haver outra em voo?
 *
 * "Carregar mais" cede sempre: ali a página anterior ainda é a base. Página 1
 * SUPERA o que estiver em voo quando a consulta mudou — ela é a resposta a um
 * corte, uma ordem ou uma sessão novos, e recusá-la deixaria a lista presa na
 * consulta que o viajante abandonou.
 *
 * Mas a MESMA página 1 já em voo não é consulta nova: é a mesma pergunta duas
 * vezes, e cada uma custa um refino de ~25s no fornecedor. Acontece sempre que o
 * efeito remonta sem a consulta mudar — o duplo-invoke do StrictMode em dev, ou
 * o painel fechado e reaberto antes de a página voltar. Medido: dois refinos
 * concorrentes, e o segundo pegou um 502 do fornecedor que apagou a página que o
 * primeiro já tinha trazido.
 */
function skipLoad(input: {
  loading: boolean;
  next: number;
  requestKey: string;
  inFlight: string | null;
}): boolean {
  if (!input.loading) return false;
  if (input.next !== 1) return true;
  return input.inFlight === input.requestKey;
}

/**
 * As páginas da lista completa por trás do scroll infinito do painel.
 *
 * A busca inicial NÃO é a página 1 disto: ela roda no modo resumido do
 * fornecedor (lista curta de prioritários, que ele se recusa a paginar), então
 * abrir o painel dispara a página 1 da lista completa. Aqui a página 1
 * SUBSTITUI o acumulado das páginas; juntá-la ao bloco da busca é decisão de
 * quem desenha (o `panelSourceOffers` do genui-kit, #3727), porque só ele sabe se há corte.
 *
 * Cada mudança de corte/ordem também volta para a página 1: o conjunto mudou, e
 * continuar do número anterior devolveria a página N de outra lista.
 *
 * A leitura é do host (`fetchPage`): o kit não faz IO. Sem ela (host sem a
 * rota, storybook, testes) o hook fica em `idle` para sempre e o painel segue
 * com o que o bloco trouxe.
 */
export function useHotelOfferPages<T extends { offerId: string }, C>(input: {
  fetchPage:
    | ((
        request: HotelOfferPageRequest<C>,
        signal?: AbortSignal,
      ) => Promise<HotelOfferPageResult<T>>)
    | undefined;
  /** Quantas ofertas cabem numa página — o teto que o host e o BFF combinam. */
  pageLimit: number;
  /** O `Error.name` com que `fetchPage` diz "a busca desta sessão venceu". */
  staleErrorName: string;
  sessionId: string | undefined;
  /** O painel está aberto? Fechado não busca — a página é leitura efêmera. */
  enabled: boolean;
  query: HotelOfferPagesQuery<C>;
  /** O que mais torna as páginas lidas obsoletas, além da sessão e da consulta.
   *
   *  Existe porque uma busca NOVA pode cair na MESMA sessão: o BFF reusa a
   *  sessão do bloco e só minta outra se ela falhar. Quando isso acontece, o
   *  `sessionId` não muda e nada aqui reagia — o caso que deixava a busca
   *  vencida sem saída: "Buscar de novo" trazia ofertas novas (200) e o painel
   *  seguia com o aviso de expirada, porque a página 1 nunca era pedida de
   *  novo. Quem passa o token é o chamador, com algo que muda a cada busca (os
   *  handles das ofertas do bloco servem: a busca emite handles novos). */
  resetToken?: string;
}): HotelOfferPages<T> {
  const { fetchPage, pageLimit, staleErrorName, sessionId, enabled, query, resetToken } = input;
  const [state, setState] = useState<HotelOfferPagesState<T>>(IDLE);
  // As páginas em mãos são de UMA sessão. Uma sessão NOVA sem remontar (a busca
  // da área no lugar, #3984) não herda as ofertas da anterior enquanto a página
  // 1 dela carrega — seriam hotéis de outro conjunto em cena. Reset no RENDER
  // (como o do painel) para nenhum quadro sair com a lista velha.
  const [stateSession, setStateSession] = useState(sessionId);
  if (stateSession !== sessionId) {
    setStateSession(sessionId);
    setState(IDLE);
  }
  const sequence = useRef(0);
  const pageNumber = useRef(0);
  const loading = useRef(false);
  /** A identidade da requisição EM VOO, para distinguir "página 1 de outra
   *  pergunta" (que supera) de "a mesma página 1 outra vez" (que é desperdício
   *  de um refino inteiro no fornecedor). */
  const inFlightKey = useRef<string | null>(null);
  /** A identidade da consulta cujas páginas ESTÃO EM MÃOS. É o que faz fechar e
   *  reabrir o painel custar zero: sem ela, cada reabertura pedia a página 1 de
   *  novo — um refino inteiro (~25s) no fornecedor para trazer a mesma lista.
   *  Só uma consulta cabe aqui: trocar o corte e voltar refaz a leitura. */
  const loadedKey = useRef<string | null>(null);
  /** A aquisição que ESTA instância detém no coordenador. Cada `load` solta a
   *  anterior antes de adquirir a nova; desmontar/desabilitar também solta. O
   *  coordenador aborta a requisição só quando o ÚLTIMO interessado solta. */
  const held = useRef<AcquiredPage<HotelOfferPageResult<T>> | null>(null);
  const releaseHeld = useCallback(() => {
    const current = held.current;
    held.current = null;
    current?.release();
  }, []);
  // A consulta entra como objeto novo a cada render do painel; o que dispara
  // busca é o CONTEÚDO dela, não a identidade — daí a chave serializada, que só
  // troca a consulta lida no fetch quando ela muda. O `resetToken` entra
  // na MESMA chave: com ele de fora, uma busca nova de consulta igual era lida
  // como repetição e a página 1 dela morria enquanto a da busca velha ainda
  // estava em voo — e era a resposta velha que terminava em cena.
  const requestKey = JSON.stringify([query, resetToken, sessionId]);
  const requestQuery = useKeyedValue(query, requestKey);

  const load = useCallback(
    async (next: number) => {
      if (!fetchPage || !sessionId) return;
      if (skipLoad({ loading: loading.current, next, requestKey, inFlight: inFlightKey.current })) {
        return;
      }
      const current = ++sequence.current;
      loading.current = true;
      inFlightKey.current = requestKey;
      // Página 1 nova ABANDONA o número já andado: sem isso, uma primeira
      // página que falha depois de o scroll ter chegado à 3 deixaria o `retry`
      // pedindo a 4 — página de um conjunto que não é mais o pedido.
      if (next === 1) pageNumber.current = 0;
      setState((previous) => ({
        ...previous,
        phase: next === 1 ? 'loading' : 'loadingMore',
      }));
      const { cuts, order } = requestQuery;
      // Coordenação por (sessionId, page, cuts, order): chave IDÊNTICA colapsa na
      // requisição em voo (N painéis do mesmo sessionId = 1 refine); chave NOVA
      // dispara já e solta a anterior desta instância (o coordenador aborta se
      // ninguém mais a quer). É o que impede N instâncias / M churns de virarem
      // N/M refines concorrentes contra a mesma sessão (#2242). NUNCA serializa
      // chaves diferentes — só colapsa idênticas e aborta superseded.
      //
      // A chave do FORNECEDOR é (sessionId, page, cuts, order) — o `resetToken`
      // NÃO entra: ele é o gatilho de re-fetch do hook ("Buscar de novo" emite
      // handles novos na mesma sessão), não parte do que se pergunta. Soltar a
      // anterior + adquirir cobre os dois churns: consulta nova (coalesceKey
      // diferente → a antiga morre) e busca refeita (mesma chave → supersede a
      // page 1 em voo). N painéis com a MESMA chave colapsam numa requisição só.
      releaseHeld();
      const key = hotelPageCoalesceKey({ sessionId, page: next, cuts, order });
      const acquired = acquireHotelPage(key, (signal) =>
        fetchPage(
          {
            sessionId,
            page: { number: next, limit: pageLimit },
            ...(cuts ? { cuts } : {}),
            ...(order ? { order } : {}),
          },
          signal,
        ),
      );
      held.current = acquired;
      try {
        const result = await acquired.promise;
        if (sequence.current !== current) return;
        pageNumber.current = next;
        loadedKey.current = requestKey;
        setState((previous) => ({
          offers:
            next === 1 ? [...result.offers] : mergeHotelOffers(previous.offers, result.offers),
          phase: 'ready',
          hasMore: result.page.hasNext,
          committed: true,
          ...(result.page.total === undefined ? {} : { total: result.page.total }),
        }));
      } catch (error) {
        if (sequence.current !== current) return;
        // Guard de AbortError — INOBSERVÁVEL hoje, NÃO redundante (categoria
        // apurada por mutação do QA, #2242). O abort do CHURN chega com sequência
        // obsoleta e já morreu no guarda acima; o único AbortError que PASSA o
        // guarda é o do DESMONTE, e ali o componente já saiu — o `setState` de
        // erro abaixo seria no-op no React 18. Ou seja, o caminho RODA, só o
        // efeito é invisível. Vira NECESSÁRIO no dia em que um abort chegar com o
        // componente MONTADO (uma mudança adjacente no ciclo de release, não uma
        // API futura), e não haverá teste provando — inobservável ≠ inalcançável.
        // Fica por isso; não é o `sequence++` do desmonte que o dispensa (removê-lo
        // não muda nada: quem mascara o desmonte é o setState-no-op).
        if (isAbortError(error)) return;
        // Busca vencida: não é falha de leitura, e repetir não traz nada — o
        // conjunto morreu no fornecedor. `committed` intocado devolve o painel às
        // ofertas do bloco, e a fase diz por quê: sem ela o painel mostrava as 6
        // do bloco calado, indistinguível de paginação quebrada.
        if (error instanceof Error && error.name === staleErrorName) {
          // Vencida também é resposta em mãos: reabrir o painel repete o aviso
          // sem repetir a pergunta que já se sabe morta. Quem a ressuscita é
          // "Buscar de novo", e ela chega aqui como consulta OUTRA.
          loadedKey.current = requestKey;
          setState((previous) => ({ ...previous, phase: 'stale', hasMore: false }));
          return;
        }
        // Página SEGUINTE que falha não apaga as anteriores: o painel continua
        // navegável com o que já tem, sem o convite a carregar mais. Página 1
        // que falha limpa — o que estava em cena era de outro conjunto, e
        // mantê-lo esconderia o aviso de falha atrás de uma lista obsoleta.
        setState((previous) =>
          next === 1
            ? { offers: [], phase: 'error', hasMore: false, committed: true }
            : { ...previous, phase: 'error', hasMore: false },
        );
      } finally {
        // Só quem ainda é a requisição corrente devolve a trava: uma resposta
        // obsoleta liberando-a abriria caminho para o `loadMore` emendar página
        // na consulta que acabou de ser substituída.
        if (sequence.current === current) {
          loading.current = false;
          inFlightKey.current = null;
        }
      }
    },
    [fetchPage, pageLimit, releaseHeld, requestKey, requestQuery, sessionId, staleErrorName],
  );

  const active = enabled && Boolean(fetchPage) && Boolean(sessionId);
  // A fase, porém, tem de ASSENTAR ao fechar: a resposta que a baixaria morre no
  // guarda de sequência, e reabrir com `loadingMore` pendurado desativa o
  // rodapé de "carregar mais" (e a sentinela que o observa) para sempre — as
  // páginas em mãos ficariam sendo as últimas que o painel mostra. During render.
  if (!active && (state.phase === 'loading' || state.phase === 'loadingMore')) {
    setState({ ...state, phase: state.committed ? 'ready' : 'idle' });
  }

  useEffect(() => {
    if (!enabled || !fetchPage || !sessionId) {
      // Fechar ABANDONA o que está em voo, mas não joga fora o que já voltou: as
      // páginas seguem válidas enquanto a consulta for a mesma, e é isso que faz
      // reabrir o painel pintar na hora. O que voltar depois disto morre no
      // guarda de sequência, então `loadedKey` fica como estava — a leitura
      // interrompida não conta como resposta em mãos.
      sequence.current += 1;
      loading.current = false;
      inFlightKey.current = null;
      // Desabilitar/fechar solta a aquisição AGORA (o load só a substitui quando
      // a superfície reabre). NÃO é `return` de cleanup: o cleanup dispararia
      // também quando só o `resetToken` oscila (mesma pergunta ao fornecedor —
      // o caminho fullscreen da bancada alimenta offers por prop), e aí a
      // reutilização da requisição em voo seria anulada, virando um refine por
      // tick. O churn de CONSULTA (coalesceKey nova) é solto dentro do `load`;
      // o desmonte, no efeito de ciclo de vida abaixo.
      releaseHeld();
      return;
    }
    if (loadedKey.current === requestKey) return;
    void load(1);
  }, [enabled, fetchPage, load, releaseHeld, requestKey, sessionId]);

  // Solta a aquisição no DESMONTE — deps estáveis, então o cleanup roda ao sair.
  // Reseta TAMBÉM as travas de instância (como o branch !enabled acima), e por
  // uma razão de StrictMode: no double-invoke (mount→unmount→mount do mesmo
  // commit em dev), este cleanup aborta a requisição do 1º mount, mas a rejeição
  // do abort é uma microtask POSTERIOR — se as travas não forem zeradas aqui,
  // o `load(1)` do remount roda antes dela e o `skipLoad` o pula (vê `loading`
  // ainda true e o `inFlightKey` da requisição já morta), deixando o painel
  // preso em `loading` sem nenhuma requisição viva. Zerar aqui deixa o remount
  // disparar — em StrictMode viram 2 requests (1 abortada + 1 viva), que é o
  // comportamento padrão do React em dev. Em prod o cleanup só roda no desmonte
  // real, onde o reset é inócuo.
  //
  // Das três linhas: `loading`/`inFlightKey` são o que destrava o remount (sem
  // elas o skipLoad pula). O `sequence.current += 1` NÃO é exercitado pela
  // suíte (removê-lo não muda teste — o guard de AbortError e o setState-no-op
  // já cobrem a rejeição do abort de desmonte); fica por SIMETRIA com o branch
  // !enabled, que faz o mesmo trio, e como defesa dupla de "resposta obsoleta".
  useEffect(
    () => () => {
      sequence.current += 1;
      loading.current = false;
      inFlightKey.current = null;
      releaseHeld();
    },
    [releaseHeld],
  );

  const loadMore = useCallback(() => {
    if (!state.hasMore || loading.current) return;
    void load(pageNumber.current + 1);
  }, [load, state.hasMore]);

  /** Refaz a página que falhou — a primeira quando nada carregou, a seguinte
   *  quando o scroll já tinha trazido alguma. */
  const retry = useCallback(() => {
    void load(pageNumber.current + 1);
  }, [load]);

  // A identidade da CONSULTA em cena (sessão + busca + corte/ordem), exposta
  // para quem precisa saber que o conjunto trocou sem duplicar a conta — o
  // mapa cheio a usa para descartar o aviso de "todos carregados".
  return { ...state, requestKey, loadMore, retry };
}
